/** Minimal path-based SPA router. Internal links use `data-link`; everything
 *  else (e.g. /auth/login, /api/.../launch) is a normal full navigation. */

type Params = Record<string, string>;
type Handler = (params: Params) => void | Promise<void>;

interface Route {
  pattern: RegExp;
  keys: string[];
  handler: Handler;
}

function compile(path: string): { pattern: RegExp; keys: string[] } {
  const keys: string[] = [];
  const source = path.replace(/:[^/]+/g, (m) => {
    keys.push(m.slice(1));
    return "([^/]+)";
  });
  return { pattern: new RegExp(`^${source}/?$`), keys };
}

export function navigate(path: string): void {
  if (path !== location.pathname + location.search) {
    history.pushState({}, "", path);
  }
  window.dispatchEvent(new PopStateEvent("popstate"));
}

export class Router {
  private routes: Route[] = [];

  add(path: string, handler: Handler): this {
    const { pattern, keys } = compile(path);
    this.routes.push({ pattern, keys, handler });
    return this;
  }

  start(): void {
    document.addEventListener("click", (e) => {
      const target = e.target as HTMLElement;
      const a = target.closest("a[data-link]") as HTMLAnchorElement | null;
      if (!a) return;
      const href = a.getAttribute("href");
      if (!href || /^https?:\/\//.test(href)) return;
      e.preventDefault();
      navigate(href);
    });
    window.addEventListener("popstate", () => void this.resolve(location.pathname));
    void this.resolve(location.pathname);
  }

  private async resolve(path: string): Promise<void> {
    for (const route of this.routes) {
      const match = route.pattern.exec(path);
      if (!match) continue;
      const params: Params = {};
      route.keys.forEach((k, i) => (params[k] = decodeURIComponent(match[i + 1])));
      await route.handler(params);
      return;
    }
    if (path !== "/") navigate("/");
  }
}
